export type TokenType = "bold" | "italic" | "strikethrough" | "code" | "text"

export interface Token {
  type: TokenType
  content: string
}

// ~strikethrough~ follows GFM's flanking rules, because report prose also uses
// a bare ~ as "approximately" ("(~401 m²), pero … difiere en ~122 m²"). An
// opening ~ must be followed by text (or by punctuation, if it is itself
// preceded by a space or punctuation); a closing ~ must be preceded by text
// (or by punctuation, if it is itself followed by a space, punctuation or the
// end). The run holds no other ~, so a closer pairs with the nearest opener.
const PUNCT = String.raw`[\p{P}\p{S}]`
const SPACE_OR_PUNCT = String.raw`[\s\p{P}\p{S}]`
const WORD = String.raw`[^\s\p{P}\p{S}]`
const STRIKE_OPEN = String.raw`(?:~(?=${WORD})|(?<=^|${SPACE_OR_PUNCT})~(?=${PUNCT}))`
const STRIKE_CLOSE = String.raw`(?:(?<=${WORD})~|(?<=${PUNCT})~(?=${SPACE_OR_PUNCT}|$))`
const STRIKE = String.raw`${STRIKE_OPEN}([^~\n]+?)${STRIKE_CLOSE}`

// Order matters: ** must be checked before * to avoid partial matches
// Single * is WhatsApp-style bold; ** is Markdown-style bold — both render as bold
const PATTERN = new RegExp(
  String.raw`\*\*(.+?)\*\*|(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)|(?<!\w)_(.+?)_(?!\w)|${STRIKE}|` + "`(.+?)`",
  "gu"
)

export function tokenize(text: string): Token[] {
  const tokens: Token[] = []
  const pattern = new RegExp(PATTERN)

  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      tokens.push({ type: "text", content: text.slice(lastIndex, match.index) })
    }

    if (match[1] !== undefined) {
      tokens.push({ type: "bold", content: match[1] })
    } else if (match[2] !== undefined) {
      tokens.push({ type: "bold", content: match[2] })
    } else if (match[3] !== undefined) {
      tokens.push({ type: "italic", content: match[3] })
    } else if (match[4] !== undefined) {
      tokens.push({ type: "strikethrough", content: match[4] })
    } else if (match[5] !== undefined) {
      tokens.push({ type: "code", content: match[5] })
    }

    lastIndex = match.index + match[0].length
  }

  if (lastIndex < text.length) {
    tokens.push({ type: "text", content: text.slice(lastIndex) })
  }

  return tokens
}
